import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { DEBUG } from '../config/debug';
import PropTypes from 'prop-types';
import { useSecurityTypes } from '../services/api';

export const SECURITY_TYPES = [
  {
    id: 'EQUITY',
    label: 'Stocks',
    quoteTypes: ['EQUITY']
  },
  {
    id: 'ETF',
    label: 'ETFs',
    quoteTypes: ['ETF']
  },
  {
    id: 'FUND',
    label: 'Funds',
    quoteTypes: ['MUTUALFUND', 'MONEYMARKET']
  }
];

const TypeFilter = ({ activeFilters, onApplyFilters }) => {
  const { securityTypes, isLoading } = useSecurityTypes();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(activeFilters || []);

  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers: [{ name: 'offset', options: { offset: [0, 4] }}],
  });

  useEffect(() => {
    if (DEBUG.filters) {
      console.log('TypeFilter: activeFilters updated:', activeFilters);
    }
    setSelectedTypes(activeFilters || []);
  }, [activeFilters]);

  // Merge static SECURITY_TYPES with fetched counts
  const typesWithCounts = SECURITY_TYPES.map(type => {
    const fetched = securityTypes.find(st => st.id === type.id);
    return {
      ...type,
      count: fetched?.count || 0
    };
  });

  const handleSelectAll = () => {
    // Toggle: if all selected, unselect all; otherwise select all
    if (selectedTypes.length === SECURITY_TYPES.length) {
      setSelectedTypes([]);
    } else {
      setSelectedTypes(SECURITY_TYPES.map(type => type.id));
    }
  };

  const handleTypeChange = (typeId) => {
    setSelectedTypes(prev =>
      prev.includes(typeId)
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    );
  };

  const handleApply = () => {
    if (DEBUG.filters) {
      console.log('TypeFilter: Applying filters:', {
        types: selectedTypes
      });
    }

    onApplyFilters({
      types: selectedTypes
    });
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedTypes(activeFilters || []);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block">
      <div
        ref={setReferenceElement}
        onClick={() => setIsOpen(!isOpen)}
        className="cursor-pointer p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
      >
        <FontAwesomeIcon
          icon={faLayerGroup}
          className={`text-lg ${selectedTypes.length !== SECURITY_TYPES.length ? 'text-blue-500' : 'text-gray-600'}`}
        />
      </div>

      {isOpen && createPortal(
        <div
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
          className="w-64 bg-white shadow-lg ring-1 ring-black/5 rounded-lg z-50 divide-y divide-gray-100"
        >
          <div className="px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-3 hover:bg-gray-50 rounded-md p-2 cursor-pointer transition-colors duration-200">
                <div className="flex items-center justify-center w-5 h-5">
                  <input
                    type="checkbox"
                    checked={selectedTypes.length === SECURITY_TYPES.length}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-colors duration-200"
                  />
                </div>
                <span className="font-medium text-sm text-gray-700">SELECT ALL</span>
              </label>
              {isLoading && (
                <FontAwesomeIcon icon={faSpinner} className="text-blue-500 animate-spin" />
              )}
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto py-2">
            {typesWithCounts.map((type) => (
              <label
                key={type.id}
                className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors duration-200"
              >
                <div className="flex items-center justify-center w-5 h-5">
                  <input
                    type="checkbox"
                    checked={selectedTypes.includes(type.id)}
                    onChange={() => handleTypeChange(type.id)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-colors duration-200"
                  />
                </div>
                <span className="text-sm text-gray-700">{type.label}</span>
                {type.count > 0 && (
                  <span className="text-xs text-gray-400 ml-auto">{type.count.toLocaleString()}</span>
                )}
              </label>
            ))}
          </div>

          <div className="p-4 bg-gray-50 rounded-b-lg flex justify-between items-center">
            <div className="text-sm text-gray-500 font-medium">
              {selectedTypes.length} Selected
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="px-4 py-1.5 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200 font-medium"
              >
                Apply
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

TypeFilter.propTypes = {
  activeFilters: PropTypes.arrayOf(PropTypes.string),
  onApplyFilters: PropTypes.func.isRequired
};

TypeFilter.defaultProps = {
  activeFilters: []
};

export default React.memo(TypeFilter, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.activeFilters) === JSON.stringify(nextProps.activeFilters);
});
